import React, { FC, useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Button from "../atoms/Button";
import CommonForm from "../molecules/CommonForm";
import { toast } from "react-toastify";
import EditIcon from '../../static/images/Editicon.svg';
import DeleteIcon from '../../static/images/Archive.svg';
import '../../static/css/CrewDetails.css';
import Spinner from "../atoms/Spinner";
import { service } from "../../../services/AxiosServices";
import SectionHeader from "./SectionHeader";
import WindowHeightComponent from "../../Layouts/WindowHeight";
import Multiselect from "multiselect-react-dropdown";

import x from '../../static/images/x.png'
import { format, parseISO } from 'date-fns'; 
import ConfirmDialog from "../atoms/ConfirmDialog";
import AccessControl from "./AccessControl";
import AccessDenied from "../atoms/AccessDenied";
import useAccessCheckLoading from "../../../utils/useAccessCheckLoading";


interface CrewNameProps {
  crewName: string;
}

const CrewDetails: FC<CrewNameProps> = ({ crewName }) => {
  const { id } = useParams<{ id: string }>();
  const crewId = Number(id);
  const navigate = useNavigate();

  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [users, setUsers] = useState<{ id: number; name: string }[]>([]);
  const [showDelete,setShowDelete] = useState(false)
  const accessCheckLoading = useAccessCheckLoading()
  const [formState, setFormState] = useState({
    values: {
      crewName: "",
      description: "",
      created_by: "",
      last_updated: "",
      associated_users: [] as number[]
    },
    clientErrors: {} as Record<string, string>,
    serverError: null as string | null,
  });
  const [initialValues, setInitialValues] = useState(formState.values);

  const fields = [
    { name: "crewName", type: "text", label: "Crew name", required: true },
    { name: "description", type: "text", label: "Description", required: true },
  ];

  // Function to get user names from selected user IDs
  const getUserNames = (userIds: number[]) => {
    return userIds
      .map((id) => {
        const user = users.find((u) => u.id === id);
        return user ? user.name : "";
      })
      .filter((name) => name !== "")
      .join(", ");
  };

  const validations = (values: Record<string, any>) => {
    const errors: Record<string, string> = {};
    if (!values.crewName.trim()) errors.crewName = "Crew Name is required.";
    if (!values.description.trim()) errors.description = "Description is required.";
    return errors;
  };


  // Fetch users list
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await service<{ response: any }>("/user-list", "post");
        const usersData = response.data.response
          .map((user: any) => ({
            id: user.id,
            name: `${(user.first_name || "").trim()} ${(user.last_name || "").trim()}`.trim() || user.email,
          }))
          .sort((a: any, b: any) => a.name.localeCompare(b.name));
        setUsers(usersData);
      } catch (err) {
        console.error("Error fetching users:", err);
      }
    };
    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchCrew = async () => {
      setLoading(true);
      try {
        const response = await service<{ response: any }>(
          'api/get-crew',
          'post',
          {
            project_id: localStorage.getItem('project_id'),
            crew_id: crewId,
          }
        );
        const crew = response.data?.response || {};

        const userIds = Array.isArray(crew.associated_users)
          ? crew.associated_users.map((user: any) => Number(user.id || user))
          : (crew.associated_users || "").split(",").map((id: string) => Number(id));

        const crewData = {
          crewName: crew.crew_name || "",
          description: crew.crew_description || "",
          created_by: crew.created_at ? format(parseISO(crew.created_at),'dd-MM-yyyy, HH:mm') :'',
          last_updated: crew.updated_at ? format(parseISO(crew.updated_at),'dd-MM-yyyy, HH:mm') :'',
          associated_users: userIds
        };
        setFormState((prev) => ({
          ...prev,
          values: crewData
        }));
        setInitialValues(crewData);
      } catch (err) {
        console.error("Error fetching crew:", err);
      } finally {
        setLoading(false);
      }
    };

    if (crewId) fetchCrew();
  }, [crewId]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setFormState((prev) => ({
        ...prev,
        values: { ...prev.values, [name]: value },
        clientErrors: { ...prev.clientErrors, [name]: "" },
        serverError: null,
      }));
    },
    []
  );

  const handleUserSelect = useCallback((selectedList: any[]) => {
    const selectedIds = selectedList.map((user) => user.id);
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, associated_users: selectedIds },
      clientErrors: { ...prev.clientErrors, associated_users: "" },
    }));
  }, []);

  const handleUserRemove = useCallback((removedList: any[]) => {
    const remainingIds = removedList.map((user) => user.id);
    setFormState((prev) => ({
      ...prev,
      values: { ...prev.values, associated_users: remainingIds },
      clientErrors: { ...prev.clientErrors, associated_users: "" },
    }));
  }, []);

  const handleEdit = () => {
    setFormState((prev) => ({ ...prev, values: { ...prev.values } }));
    setIsEdit(true);
  };

  const handleCancel = () => {
    setFormState((prev) => ({
      ...prev,
      values: initialValues,
    }));
    setIsEdit(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors = validations(formState.values);
    if (Object.keys(errors).length === 0) {
      try {
        const response = await service<{ response: any }>(
          'api/create-crew',
          'post',
          {
            project_id: localStorage.getItem('project_id'),
    
            ...(crewId && {crew_id: crewId, }),
            crew_name: formState.values.crewName,
            crew_description: formState.values.description,
            created_updated_by: localStorage.getItem("user"),
            associated_users: formState.values.associated_users.join(",")
          }
        );
        const crew = response.data?.response || "";
      

        setIsEdit(false);
        toast.success("Crew updated successfully!", { autoClose: 2000 });
        navigate(`/edit_crew/${crewId}`, {
          state: { crewName: formState.values.crewName },
        });
      } catch (err: any) {
        const errorMessage = err.response?.data?.error || "Something went wrong. Please try again..";
        setFormState((prev) => ({ ...prev, serverError: errorMessage }));
      }
    } else {
      setFormState((prev) => ({ ...prev, clientErrors: errors }));
    }
  };
const handleDelete = ()=>{
  setShowDelete(true)
}
  const confirmDelete = async () => {
    if (!crewId) return;
    try {
      setLoading(true);
      await service(
        "/api/crew/delete",
        "delete",
        null,
        "application/json",
        crewId
      );
      toast.success("Crew deleted successfully!", { autoClose: 2000 });
      navigate("/crews");
    } catch (err) {
      console.error("Error deleting crew:", err);
      toast.error("Failed to delete crew. Please try again.");
    } finally {
      setLoading(false);
      setShowDelete(false)
    }
  };

  const clientErrorsArray = Object.entries(formState.clientErrors).reduce((acc: string[], [field, error]) => {
    if (error) {
      acc.push(`${error}`);
    }
    return acc;
  }, []);

  return (
    <>
    <AccessControl
    requiredPermissions={[
          {
            permission: "create_crew",
            types: isEdit? ["update"]:["read"],
          },
        ]}
        renderNoAccess={ accessCheckLoading ? <Spinner/> :<AccessDenied />}
      >
      <SectionHeader back={true} title={isEdit ? 'Edit crew' : 'Crew details'}></SectionHeader>
      <WindowHeightComponent className={""}>
        <div className="container-fluid">
          <div className="row">
            <div className="p-3">
              <div className="d-flex justify-content-end">
                {!isEdit && (
                  <>
              <AccessControl
                  requiredPermissions={[
                    {
                      permission: "create_crew",
                      types: ["update"],
                    },
                  ]}
                  renderNoAccess={<></>}
                >
                
                   <button onClick={handleEdit} className="me-1" type="button">
                      <img src={EditIcon} alt="Edit" title="Edit" data-bs-toggle="tooltip" className="icon me-1" />
                    </button>
                    
                </AccessControl>

                <AccessControl
                  requiredPermissions={[
                    {
                      permission: "create_crew",
                      types: ["delete"],
                    },
                  ]}
                  renderNoAccess={<></>}
                >
                
                   <button onClick={handleDelete} type="button">
                      <img src={DeleteIcon} alt="Delete" title="Delete" data-bs-toggle="tooltip" className="icon me-1" />
                    </button>
                    
                </AccessControl>
                  </>
                )}
              </div>
              <div className="crew-details my-1">
                {loading ? (
                  <Spinner />
                ) : isEdit ? (
                  <>
                    {formState.serverError && (
                      <div className="alert alert-danger">{formState.serverError}</div>
                    )}
                    {clientErrorsArray.length > 0 && (
                      <div className="alert alert-danger">
                        <ul>
                          {clientErrorsArray.map((error, index) => (
                            <li key={index}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <CommonForm
                      fields={fields}
                      values={formState.values}
                      onChange={handleChange}
                      onSubmit={handleSubmit}
                      onCancel={handleCancel}
                      isEdit={isEdit}
                    >
                      <div className="">
                        <label htmlFor="associated_users" className="LabelText mb-1">
                          Select users
                        </label>
                        <Multiselect
                          options={users}
                          selectedValues={users.filter((user) =>
                            formState.values.associated_users.includes(user.id)
                          )}
                          onSelect={handleUserSelect}
                          onRemove={handleUserRemove}
                          displayValue="name"
                          id="associated_users"
                          customCloseIcon={<img style={{ width: "1rem", height: "1rem", marginLeft:'1rem' }} src={x} alt="close" />}
                          
                        />
                      </div>
                    </CommonForm>
                  </>
                ) : (
                  <>
                  
                    <div className="details-container">
                      <div className="detail-item">
                        <span className="field-label">Crew name:</span>
                        <span className="field-value">{formState.values.crewName}</span>
                      </div>
                      <div className="detail-item">
                        <span className="field-label">Crew description:</span>
                        <span className="field-value">{formState.values.description}</span>
                      </div>
                      <div className="detail-item">
                        <span className="field-label">Created on:</span>
                        <span className="field-value">{formState.values.created_by}</span>
                      </div>
                      <div className="detail-item">
                        <span className="field-label">Used LLM:</span>
                        <span className="field-value">{'OpenAI'}</span>
                      </div>
                      <div className="detail-item">
                        <span className="field-label">Updated on:</span>
                        <span className="field-value">{formState.values.last_updated}</span>
                      </div>
                      <div className="detail-item">
                        <span className="field-label">Associated users:</span>
                        <span className="field-value">
                          {getUserNames(formState.values.associated_users)}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>


        <ConfirmDialog
        show={showDelete}
        onHide={() => setShowDelete(false)}
        onConfirm={confirmDelete}
        title="Confirm delete"
        body="Are you sure?"
        confirmText="Delete"
      />

      </WindowHeightComponent>
      </AccessControl>
    </>
  );
};

export default CrewDetails;